"use client";

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ExternalLink,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Copy as CopyIcon,
  Trash2,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { Avatar } from "@/components/ui/Avatar";
import { Markdown } from "@/components/ui/Markdown";
import { formatInZone, formatRelativeTime } from "@/lib/timezone";
import type { FeedbackSeverity, FeedbackStatus } from "@/lib/types";

const STATUS_LABEL: Record<FeedbackStatus, { label: string; color: string }> = {
  new: { label: "New", color: "var(--accent)" },
  triaged: { label: "Triaged", color: "var(--blue)" },
  resolved: { label: "Resolved", color: "var(--green)" },
  wontfix: { label: "Won't fix", color: "var(--fg-muted)" },
  duplicate: { label: "Duplicate", color: "var(--fg-muted)" },
};

const SEVERITY: Record<FeedbackSeverity, { label: string; color: string; icon: typeof AlertCircle }> = {
  high: { label: "High severity", color: "var(--red)", icon: AlertCircle },
  medium: { label: "Medium severity", color: "var(--orange)", icon: AlertTriangle },
  low: { label: "Low severity", color: "var(--fg-muted)", icon: Info },
};

export function FeedbackDetail({ canWrite }: { canWrite: boolean }) {
  const selectedId = useStore((s) => s.selectedFeedbackId);
  const feedback = useStore((s) =>
    s.feedback.find((f) => f.id === s.selectedFeedbackId)
  );
  const selectFeedback = useStore((s) => s.selectFeedback);
  const triageFeedback = useStore((s) => s.triageFeedback);
  const deleteFeedback = useStore((s) => s.deleteFeedback);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const workspaces = useStore((s) => s.workspaces);
  const teams = useStore((s) => s.teams);
  const issues = useStore((s) => s.issues);
  const docs = useStore((s) => s.docs);
  const users = useStore((s) => s.users);
  const selectIssue = useStore((s) => s.selectIssue);
  const selectDoc = useStore((s) => s.selectDoc);

  const [busy, setBusy] = useState(false);
  const [pickIssueOpen, setPickIssueOpen] = useState(false);
  const [issuePickerQ, setIssuePickerQ] = useState("");

  const tz =
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") selectFeedback(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, selectFeedback]);

  const visible = Boolean(selectedId && feedback);

  const isTarget = feedback?.targetWorkspaceId === activeWorkspaceId;
  const isSource = feedback?.sourceWorkspaceId === activeWorkspaceId;

  const sourceWs = useMemo(
    () => workspaces.find((w) => w.id === feedback?.sourceWorkspaceId),
    [workspaces, feedback?.sourceWorkspaceId]
  );
  const targetWs = useMemo(
    () => workspaces.find((w) => w.id === feedback?.targetWorkspaceId),
    [workspaces, feedback?.targetWorkspaceId]
  );
  const submitter = users.find((u) => u.id === feedback?.submitterId);
  const triager = users.find((u) => u.id === feedback?.triagedBy);
  const linkedIssue = feedback?.linkedIssueId
    ? issues.find((i) => i.id === feedback.linkedIssueId)
    : null;
  const sourceDoc = feedback?.sourceDocId
    ? docs.find((d) => d.id === feedback.sourceDocId)
    : null;

  const wsTeams = useMemo(
    () => teams.filter((t) => t.workspaceId === activeWorkspaceId),
    [teams, activeWorkspaceId]
  );

  if (!feedback) return <AnimatePresence />;

  const StatusM = STATUS_LABEL[feedback.status];
  const SevM = SEVERITY[feedback.severity];

  async function doTriage(action: Parameters<typeof triageFeedback>[1]) {
    if (!canWrite || !feedback) return;
    setBusy(true);
    await triageFeedback(feedback.id, action);
    setBusy(false);
    setPickIssueOpen(false);
  }

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            key={`${feedback.id}-backdrop`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => selectFeedback(null)}
            className="fixed inset-0 z-30 bg-black/30"
          />
          <motion.div
            key={feedback.id}
            initial={{ x: 80, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed top-0 right-0 bottom-0 w-[820px] max-w-[100vw] z-40 bg-[var(--bg)] border-l border-[var(--border)] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
              <SevM.icon size={14} style={{ color: SevM.color }} />
              <span className="text-[11px] text-[var(--fg-muted)]">
                {sourceWs?.name ?? "?"} <ArrowRight size={10} className="inline -mt-0.5" />{" "}
                {targetWs?.name ?? "?"}
              </span>
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{
                  background: `color-mix(in srgb, ${StatusM.color} 18%, transparent)`,
                  color: StatusM.color,
                }}
              >
                {StatusM.label}
              </span>
              {linkedIssue && (
                <button
                  onClick={() => selectIssue(linkedIssue.id)}
                  className="px-1.5 py-0.5 rounded bg-[var(--accent-bg)] text-[var(--accent)] text-[10px] font-mono hover:bg-[var(--accent-bg)]/80"
                >
                  &rarr; {linkedIssue.identifier}
                </button>
              )}
              <button
                onClick={() => selectFeedback(null)}
                className="ml-auto w-7 h-7 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
              >
                <X size={13} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              <div className="px-6 py-5 space-y-4">
                <h1 className="text-[20px] font-semibold leading-tight">{feedback.title}</h1>

                <div className="flex items-center gap-3 text-[11.5px] text-[var(--fg-tertiary)]">
                  {submitter && (
                    <span className="flex items-center gap-1.5">
                      <Avatar user={submitter} size={18} />
                      submitted by <span className="text-[var(--fg-secondary)]">{submitter.name}</span>
                    </span>
                  )}
                  <span title={formatInZone(feedback.createdAt, tz, true)}>
                    {formatRelativeTime(feedback.createdAt)}
                  </span>
                  {feedback.tags.length > 0 && (
                    <div className="flex items-center gap-1">
                      {feedback.tags.map((t) => (
                        <span
                          key={t}
                          className="text-[10.5px] text-[var(--fg-secondary)] bg-[var(--bg-tertiary)] px-1.5 py-0.5 rounded-full"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {feedback.body.trim() && (
                  <div className="prose-doc rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-4 py-3">
                    <Markdown content={feedback.body} />
                  </div>
                )}

                {sourceDoc && (
                  <button
                    onClick={() => selectDoc(sourceDoc.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--accent)] transition-colors text-left"
                  >
                    <ExternalLink size={12} className="text-[var(--accent)]" />
                    <span className="text-[12px]">
                      Source doc:{" "}
                      <span className="text-[var(--accent)] font-medium">{sourceDoc.title}</span>
                    </span>
                    <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
                      in {sourceWs?.name}
                    </span>
                  </button>
                )}

                {!sourceDoc && feedback.sourceDocId && (
                  <div className="text-[11px] text-[var(--fg-muted)] italic">
                    Source doc lives in {sourceWs?.name} (you don&apos;t have access).
                  </div>
                )}

                {/* Triage actions (target side, status=new) */}
                {canWrite && isTarget && feedback.status === "new" && (
                  <div className="rounded-md border border-[var(--accent)]/30 bg-[var(--accent-bg)]/30 p-4 space-y-3">
                    <div className="text-[12px] font-semibold text-[var(--fg)]">Triage actions</div>
                    {pickIssueOpen ? (
                      <div className="space-y-2">
                        <input
                          autoFocus
                          value={issuePickerQ}
                          onChange={(e) => setIssuePickerQ(e.target.value)}
                          placeholder="Search tasks by title or identifier..."
                          className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
                        />
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {issues
                            .filter(
                              (i) =>
                                i.workspaceId === activeWorkspaceId &&
                                (i.title.toLowerCase().includes(issuePickerQ.toLowerCase()) ||
                                  i.identifier.toLowerCase().includes(issuePickerQ.toLowerCase()))
                            )
                            .slice(0, 12)
                            .map((i) => (
                              <button
                                key={i.id}
                                onClick={() =>
                                  doTriage({ action: "link_existing_issue", issueId: i.id })
                                }
                                className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--bg-hover)] flex items-center gap-2"
                              >
                                <span className="font-mono text-[10.5px] text-[var(--accent)]">
                                  {i.identifier}
                                </span>
                                <span className="text-[12px] truncate">{i.title}</span>
                              </button>
                            ))}
                        </div>
                        <button
                          onClick={() => setPickIssueOpen(false)}
                          className="text-[11px] text-[var(--fg-tertiary)] hover:text-[var(--fg-secondary)]"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() =>
                            doTriage({
                              action: "convert_to_issue",
                              teamId: wsTeams.length === 1 ? wsTeams[0].id : undefined,
                            })
                          }
                          disabled={busy || (wsTeams.length !== 1)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[12px] font-medium"
                          title={
                            wsTeams.length !== 1
                              ? "Workspace has multiple teams — pick via MCP triage_feedback for now."
                              : "Create a new task from this feedback"
                          }
                        >
                          {busy ? <Loader2 size={11} className="animate-spin" /> : <ArrowRight size={11} />}
                          Convert to task
                        </button>
                        <button
                          onClick={() => setPickIssueOpen(true)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] font-medium text-[var(--fg-secondary)]"
                        >
                          <CopyIcon size={11} />
                          Link existing
                        </button>
                        <button
                          onClick={() => doTriage({ action: "resolve" })}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] font-medium text-[var(--green)]"
                        >
                          <CheckCircle2 size={11} />
                          Resolve
                        </button>
                        <button
                          onClick={() => doTriage({ action: "duplicate" })}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] font-medium text-[var(--fg-tertiary)]"
                        >
                          <CopyIcon size={11} />
                          Duplicate
                        </button>
                        <button
                          onClick={() => doTriage({ action: "wontfix" })}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] font-medium text-[var(--red)]"
                        >
                          <XCircle size={11} />
                          Won&apos;t fix
                        </button>
                      </div>
                    )}
                    {wsTeams.length !== 1 && (
                      <div className="text-[10.5px] text-[var(--fg-muted)]">
                        Workspace has {wsTeams.length} teams — UI picker coming soon. Use{" "}
                        <code>triage_feedback</code> via MCP for now.
                      </div>
                    )}
                  </div>
                )}

                {/* Outbox info (source side, status != new) */}
                {isSource && feedback.status !== "new" && (
                  <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-3 text-[12px] text-[var(--fg-secondary)]">
                    <div>
                      Status: <strong style={{ color: StatusM.color }}>{StatusM.label}</strong>
                      {triager && (
                        <>
                          {" "}
                          by <span className="text-[var(--fg)]">{triager.name}</span>
                          {feedback.triagedAt && (
                            <span className="text-[var(--fg-muted)]">
                              {" "}
                              · {formatRelativeTime(feedback.triagedAt)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {linkedIssue && (
                      <div className="mt-1 text-[11.5px]">
                        Linked task:{" "}
                        <button
                          onClick={() => selectIssue(linkedIssue.id)}
                          className="text-[var(--accent)] font-mono hover:underline"
                        >
                          {linkedIssue.identifier}
                        </button>{" "}
                        — {linkedIssue.title}
                      </div>
                    )}
                  </div>
                )}

                {/* Source-side delete (only while new) */}
                {canWrite && isSource && feedback.status === "new" && (
                  <button
                    onClick={async () => {
                      if (!canWrite || !feedback) return;
                      if (!confirm("Retract this feedback? It will be removed from the dev team's queue.")) return;
                      await deleteFeedback(feedback.id);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--red)]/30 hover:bg-[var(--red)]/10 text-[12px] font-medium text-[var(--red)]"
                  >
                    <Trash2 size={11} />
                    Retract submission
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
